import React, { useState, useMemo, useRef, useEffect } from 'react'
import { format, parseISO, addDays, startOfWeek } from 'date-fns'
import type { PrivateAppointment } from '@/features/admin/tabs/mon-espace/types/monEspace.types'
import './AvailabilityCalendar.styles.css'

interface TimeSlot {
  start: string
  end: string
  enabled: boolean
}

type WorkLocation = 'office' | 'remote';

interface AvailabilityCalendarProps {
  availability: Record<string, any>
  domicileAddresses: Array<{
    id: number
    name: string
    street: string
    city: string
    postalCode: string
  }>
  onAvailabilityChange: (availability: Record<string, any>) => void
  onAutoSave?: (availability: Record<string, any>) => Promise<void> // Auto-save callback
  privateAppointments?: PrivateAppointment[]
  onPrivateAppointmentsChange?: (appointments: PrivateAppointment[]) => void
  mode?: 'recurrent' | 'week' // Add mode prop for conditional rendering
  currentWeekStart?: string // Add currentWeekStart prop (YYYY-MM-DD format)
  isPastWeek?: boolean // Add isPastWeek prop to disable editing
  // Drives the Férié chip tooltip: employees get the auto-credit wording,
  // independents (default) the hours-count-as-worked wording.
  isEmployee?: boolean
}

const WEEK_DAYS = [
  { key: 'monday', label: 'Lundi', short: 'LUN' },
  { key: 'tuesday', label: 'Mardi', short: 'MAR' },
  { key: 'wednesday', label: 'Mercredi', short: 'MER' },
  { key: 'thursday', label: 'Jeudi', short: 'JEU' },
  { key: 'friday', label: 'Vendredi', short: 'VEN' },
  { key: 'saturday', label: 'Samedi', short: 'SAM' },
  { key: 'sunday', label: 'Dimanche', short: 'DIM' }
]

// Generate time slots with 15-minute intervals from 6 AM to midnight
const generateTimeSlots = () => {
  const slots = []
  for (let hour = 6; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push({
        hour,
        minute,
        time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      })
    }
  }
  return slots
}

const TIME_SLOTS = generateTimeSlots()

export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  availability,
  domicileAddresses,
  onAvailabilityChange,
  onAutoSave,
  privateAppointments = [],
  onPrivateAppointmentsChange,
  mode = 'week', // Default to week mode for backward compatibility
  currentWeekStart, // Week start date from parent component
  // Lock flag — PlanningTab passes isWeekValidated here. When true the whole
  // calendar is read-only: working hours AND appointments (create/edit/
  // resize/delete). Slot saves are also blocked at the hook level, but
  // appointment mutations have no other guard, so this must be enforced.
  isPastWeek: isLocked = false,
  isEmployee = false
}) => {
  const [selectedWeek, _setSelectedWeek] = useState(0)
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [editingAppointment, setEditingAppointment] = useState<PrivateAppointment | null>(null)
  const [editingDay, setEditingDay] = useState<string | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    day: string
    date: string
    startTime: string
    endTime: string
  } | null>(null)
  const [showWeekendDays, setShowWeekendDays] = useState(false)
  const [editingWorkingHours, setEditingWorkingHours] = useState<{
    day: string
    startTime: string
    endTime: string
  } | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Clean mode (default) shows the planning read-only; the pencil button in
  // the day-cards row switches to edit mode. A validated (locked) week can
  // never enter edit mode.
  const [isEditMode, setIsEditMode] = useState(false)
  const readOnly = isLocked || !isEditMode
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ day: string; slotIndex: number } | null>(null)
  const [dragEnd, setDragEnd] = useState<{ day: string; slotIndex: number } | null>(null)
  const timelineRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  // Resize state for appointment resizing
  const [isResizing, setIsResizing] = useState(false)
  const [resizingAppointment, setResizingAppointment] = useState<PrivateAppointment | null>(null)
  const [resizeDirection, setResizeDirection] = useState<'top' | 'bottom' | null>(null)
  const [resizeStartY, setResizeStartY] = useState(0)
  const [resizeOriginalStart, setResizeOriginalStart] = useState('')
  const [resizeOriginalEnd, setResizeOriginalEnd] = useState('')

  // Track if we're interacting with a button/handle to prevent opening modal
  const isInteractingWithControl = useRef(false)

  // Convert slots array to single slot for compatibility
  const getSlot = (day: string): TimeSlot => {
    const dayData = availability[day]
    if (dayData?.slot) {
      return dayData.slot
    } else if (dayData?.slots && Array.isArray(dayData.slots)) {
      // Convert from slots array to single slot
      const enabledSlot = dayData.slots.find((s: any) => s.enabled)
      if (enabledSlot) {
        return enabledSlot
      }
      return dayData.slots[0] || { start: '09:00', end: '17:00', enabled: false }
    }
    return { start: '09:00', end: '17:00', enabled: false }
  }

  // Get work location for a day
  const getWorkLocation = (day: string): WorkLocation => {
    const dayData = availability[day]
    return dayData?.workLocation || 'office' // Default to office
  }

  // Calculate current week dates
  const weekDates = useMemo(() => {
    // In recurrent mode, show generic week template without specific dates
    if (mode === 'recurrent') {
      return WEEK_DAYS.map((day) => ({
        ...day,
        date: '', // No specific date in recurrent mode
        dayNumber: null as any, // No day number
        month: '', // No month
        isToday: false
      }))
    }

    // In week mode, calculate dates for the current week
    const today = new Date()
    let monday: Date

    if (currentWeekStart) {
      // Use parseISO to avoid UTC parsing issues with date strings
      monday = parseISO(currentWeekStart)
    } else {
      // Fallback: calculate current week using date-fns startOfWeek (DST-safe)
      const baseMonday = startOfWeek(today, { weekStartsOn: 1 })
      monday = addDays(baseMonday, selectedWeek * 7)
    }

    const result = WEEK_DAYS.map((day, index) => {
      // Use addDays instead of setDate to avoid DST issues
      const date = addDays(monday, index)
      const formatted = format(date, 'yyyy-MM-dd')
      return {
        ...day,
        date: formatted,
        dayNumber: date.getDate(),
        month: date.toLocaleDateString('fr-BE', { month: 'short' }),
        isToday: date.toDateString() === today.toDateString()
      }
    })
    return result
  }, [selectedWeek, mode, currentWeekStart])

  const handleMouseDown = (day: string, slotIndex: number) => {
    if (readOnly) return
    // If we're editing working hours for this day, allow drag selection
    if (editingDay === day) {
      setIsDragging(true)
      setDragStart({ day, slotIndex })
      setDragEnd({ day, slotIndex })
      return
    }

    // Check if there's an appointment at this slot
    const dayData = weekDates.find(d => d.key === day)
    if (dayData) {
      const appointments = getAppointmentsForDay(dayData.date, day)
      const timeSlot = TIME_SLOTS[slotIndex]
      if (timeSlot) {
        const existingAppointment = isAppointmentInSlot(timeSlot.time, appointments)
        if (existingAppointment) {
          // Edit existing appointment instead of creating new one
          setEditingAppointment(existingAppointment)
          setShowAppointmentModal(true)
          return
        }
      }
    }

    // Start drag selection for new appointment
    setIsDragging(true)
    setDragStart({ day, slotIndex })
    setDragEnd({ day, slotIndex })
  }

  const handleMouseMove = (day: string, slotIndex: number) => {
    if (isDragging && dragStart) {
      setDragEnd({ day, slotIndex })
    }
  }

  const handleMouseUp = () => {
    if (isDragging && dragStart && dragEnd) {
      // Only allow selection within the same day
      if (dragStart.day === dragEnd.day) {
        const startIndex = Math.min(dragStart.slotIndex, dragEnd.slotIndex)
        const endIndex = Math.max(dragStart.slotIndex, dragEnd.slotIndex)

        // If we're editing working hours, update the temporary working hours
        if (editingDay === dragStart.day) {
          setEditingWorkingHours({
            day: dragStart.day,
            startTime: TIME_SLOTS[startIndex].time,
            endTime: TIME_SLOTS[Math.min(endIndex + 1, TIME_SLOTS.length - 1)].time
          })
        } else {
          // Otherwise, show appointment modal
          const dayData = weekDates.find(d => d.key === dragStart.day)
          if (dayData) {
            setSelectedTimeSlot({
              day: dragStart.day,
              date: dayData.date,
              startTime: TIME_SLOTS[startIndex].time,
              endTime: TIME_SLOTS[Math.min(endIndex + 1, TIME_SLOTS.length - 1)].time
            })
            setShowAppointmentModal(true)
          }
        }
      }
    }
    setIsDragging(false)
    setDragStart(null)
    setDragEnd(null)
  }

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp()
      }
    }
    
    document.addEventListener('mouseup', handleGlobalMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging, dragStart, dragEnd])

  // Handle click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingDay && calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        handleCancelWorkingHours()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editingDay])

  const handleTimeSlotToggle = async (day: string) => {
    if (readOnly) return
    const currentSlot = getSlot(day)
    const newAvailability = {
      ...availability,
      [day]: {
        ...availability[day],
        startingDomicile: availability[day]?.startingDomicile || 1,
        slot: { ...currentSlot, enabled: !currentSlot.enabled },
        slots: [{ ...currentSlot, enabled: !currentSlot.enabled }]
      }
    }

    // Update local state
    onAvailabilityChange(newAvailability)

    // Auto-save if callback is provided
    if (onAutoSave) {
      setIsSaving(true)
      try {
        await onAutoSave(newAvailability)
      } catch (error) {
        console.error('Error auto-saving working hours toggle:', error)
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleWorkLocationChange = async (day: string, location: WorkLocation) => {
    if (readOnly) return
    const newAvailability = {
      ...availability,
      [day]: {
        ...availability[day],
        startingDomicile: availability[day]?.startingDomicile || 1,
        slot: getSlot(day),
        slots: [getSlot(day)],
        workLocation: location
      }
    }

    // Update local state
    onAvailabilityChange(newAvailability)

    // Auto-save if callback is provided
    if (onAutoSave) {
      setIsSaving(true)
      try {
        await onAutoSave(newAvailability)
      } catch (error) {
        console.error('Error auto-saving work location:', error)
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleConfirmWorkingHours = async () => {
    if (editingWorkingHours) {
      const currentSlot = getSlot(editingWorkingHours.day)
      const currentWorkLocation = getWorkLocation(editingWorkingHours.day)
      const newAvailability = {
        ...availability,
        [editingWorkingHours.day]: {
          ...availability[editingWorkingHours.day],
          startingDomicile: availability[editingWorkingHours.day]?.startingDomicile || 1,
          workLocation: currentWorkLocation,
          slot: {
            ...currentSlot,
            start: editingWorkingHours.startTime,
            end: editingWorkingHours.endTime,
            enabled: true
          },
          slots: [{
            ...currentSlot,
            start: editingWorkingHours.startTime,
            end: editingWorkingHours.endTime,
            enabled: true
          }]
        }
      }

      // Update local state first
      onAvailabilityChange(newAvailability)

      // Auto-save if callback is provided
      if (onAutoSave) {
        setIsSaving(true)
        try {
          await onAutoSave(newAvailability)
        } catch (error) {
          console.error('Error auto-saving working hours:', error)
        } finally {
          setIsSaving(false)
        }
      }

      setEditingWorkingHours(null)
      setEditingDay(null)
    }
  }

  const handleCancelWorkingHours = () => {
    setEditingWorkingHours(null)
    setEditingDay(null)
  }

  const handleDayClick = (day: string) => {
    if (readOnly) return
    const slot = getSlot(day)
    if (slot?.enabled) {
      if (editingDay === day) {
        // Toggle off editing mode
        setEditingDay(null)
        setEditingWorkingHours(null)
      } else {
        // Enable editing mode - clear the working hours display to allow drawing
        setEditingDay(day)
        setEditingWorkingHours(null)
      }
    } else {
      setEditingDay(null)
      setEditingWorkingHours(null)
    }
  }

  const handleAddAppointment = (appointment: PrivateAppointment) => {
    if (readOnly) return
    if (onPrivateAppointmentsChange) {
      const newAppointments = editingAppointment
        ? privateAppointments.map(apt => apt.id === editingAppointment.id ? appointment : apt)
        : [...privateAppointments, { ...appointment, id: Date.now().toString() }]
      onPrivateAppointmentsChange(newAppointments)
    }
    setShowAppointmentModal(false)
    setEditingAppointment(null)
    setSelectedTimeSlot(null)
  }

  const handleDeleteAppointment = (appointmentId: string, currentDate?: string) => {
    if (readOnly) return
    if (!onPrivateAppointmentsChange) return

    const appointment = privateAppointments.find(apt => apt.id === appointmentId)
    if (!appointment) return

    // If this is an override appointment, just delete it
    if (appointment.overrideDate && appointment.overrideParentId) {
      onPrivateAppointmentsChange(privateAppointments.filter(apt => apt.id !== appointmentId))
      return
    }

    // In week mode, if appointment is recurring (template), add exception instead of deleting
    if (mode === 'week' && appointment.recurring && currentDate && !appointment.overrideParentId) {
      const updatedAppointments = privateAppointments.map(apt => {
        if (apt.id === appointmentId) {
          const exceptions = apt.exceptions || []
          // Add the date to exceptions if not already there
          if (!exceptions.includes(currentDate)) {
            return { ...apt, exceptions: [...exceptions, currentDate] }
          }
        }
        return apt
      })
      onPrivateAppointmentsChange(updatedAppointments)
    } else {
      // In recurrent mode or for non-recurring appointments, delete entirely
      onPrivateAppointmentsChange(privateAppointments.filter(apt => apt.id !== appointmentId))
    }
  }

  // Resize handlers for appointment resizing
  const handleResizeStart = (e: React.MouseEvent, appointment: PrivateAppointment, direction: 'top' | 'bottom') => {
    e.stopPropagation() // Prevent triggering click-to-edit
    e.preventDefault() // Prevent default behavior
    if (readOnly) return
    isInteractingWithControl.current = true // Mark that we're interacting with a control
    setIsResizing(true)
    setResizingAppointment(appointment)
    setResizeDirection(direction)
    setResizeStartY(e.clientY)
    setResizeOriginalStart(appointment.startTime)
    setResizeOriginalEnd(appointment.endTime)
  }

  const handleDeleteClick = (e: React.MouseEvent, appointmentId: string, currentDate?: string) => {
    e.stopPropagation() // Prevent triggering click-to-edit
    e.preventDefault() // Prevent default behavior
    isInteractingWithControl.current = true // Mark that we're interacting with a control
    handleDeleteAppointment(appointmentId, currentDate)
    // Reset the flag after a short delay to allow the click event to be ignored
    setTimeout(() => {
      isInteractingWithControl.current = false
    }, 100)
  }

  // Add resize event listeners
  useEffect(() => {
    if (!isResizing || !resizingAppointment) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingAppointment || !resizeDirection) return

      // Calculate time delta (44px per hour = 11px per 15 minutes)
      const deltaY = e.clientY - resizeStartY
      const deltaMinutes = Math.round((deltaY / 11)) * 15 // Snap to 15-minute intervals

      // Parse original times
      const [startHour, startMinute] = resizeOriginalStart.split(':').map(Number)
      const [endHour, endMinute] = resizeOriginalEnd.split(':').map(Number)

      let newStartTime = resizeOriginalStart
      let newEndTime = resizeOriginalEnd

      if (resizeDirection === 'top') {
        // Resize from top (change start time)
        const totalStartMinutes = startHour * 60 + startMinute + deltaMinutes
        const clampedMinutes = Math.max(6 * 60, Math.min(totalStartMinutes, (endHour * 60 + endMinute) - 15)) // Min 15 min duration
        const newHour = Math.floor(clampedMinutes / 60)
        const newMinute = clampedMinutes % 60
        newStartTime = `${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`
      } else {
        // Resize from bottom (change end time)
        const totalEndMinutes = endHour * 60 + endMinute + deltaMinutes
        const clampedMinutes = Math.min(24 * 60, Math.max(totalEndMinutes, (startHour * 60 + startMinute) + 15)) // Min 15 min duration
        const newHour = Math.floor(clampedMinutes / 60)
        const newMinute = clampedMinutes % 60
        newEndTime = `${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`
      }

      // Local state only during the drag — the block renders from
      // resizingAppointment for live feedback. The API write happens ONCE on
      // mouse-up; committing every 15-min step fired a PUT + cache
      // invalidation + refetch per mousemove and the refetch raced the drag.
      setResizingAppointment({
        ...resizingAppointment,
        startTime: newStartTime,
        endTime: newEndTime
      })
    }

    const handleMouseUp = () => {
      // Commit the final size once, only if it actually changed.
      if (
        onPrivateAppointmentsChange &&
        resizingAppointment &&
        (resizingAppointment.startTime !== resizeOriginalStart ||
          resizingAppointment.endTime !== resizeOriginalEnd)
      ) {
        onPrivateAppointmentsChange(
          privateAppointments.map(apt =>
            apt.id === resizingAppointment.id ? resizingAppointment : apt
          )
        )
      }
      setIsResizing(false)
      setResizingAppointment(null)
      setResizeDirection(null)
      // Reset the flag after a short delay to allow the click event to be ignored
      setTimeout(() => {
        isInteractingWithControl.current = false
      }, 100)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, resizingAppointment, resizeDirection, resizeStartY, resizeOriginalStart, resizeOriginalEnd, privateAppointments, onPrivateAppointmentsChange])

  const getAppointmentsForDay = (date: string, dayKey: string) => {
    // First, check if there are any overrides for this date
    const overrides = privateAppointments.filter(apt => apt.overrideDate === date);
    const overrideParentIds = new Set(overrides.map(apt => apt.overrideParentId));

    return privateAppointments.filter(apt => {
      // Show override appointments for this specific date
      if (apt.overrideDate === date) return true;

      // If this is a recurring appointment that has an override for this date, DON'T show it
      if (apt.recurring && overrideParentIds.has(apt.id)) return false;

      // Check if this date is in the exceptions list
      if (apt.exceptions && apt.exceptions.includes(date)) {
        return false;
      }

      // Show one-time appointments for this date
      if (apt.date === date && !apt.recurring && !apt.overrideDate) return true;

      // Show recurring appointments for matching day of week
      if (apt.recurring && apt.dayOfWeek === dayKey && !apt.overrideDate) {
        // Check if created after this date (only show from creation date forward)
        if (apt.createdAt) {
          // Use parseISO to avoid UTC parsing issues with date strings
          const createdDate = parseISO(apt.createdAt.split('T')[0]);
          const checkDate = parseISO(date);
          if (checkDate < createdDate) return false;
        }

        // Check if ended before this date
        if (apt.recurrenceEndDate) {
          // Use parseISO to avoid UTC parsing issues with date strings
          const endDate = parseISO(apt.recurrenceEndDate);
          const checkDate = parseISO(date);
          if (checkDate > endDate) return false;
        }

        // Biweekly: only show on even week offsets from the anchor date so the
        // calendar matches the worked-hours deduction (LeaveService /
        // PlanningTab). Week mode only — in recurrent (template) mode `date`
        // is '' (no concrete week to anchor against), so the template shows
        // the break on its weekday like any recurring entry. UTC midnights
        // keep the week count exact across DST transitions.
        if (apt.recurrencePattern === 'biweekly' && mode === 'week' && date) {
          const anchor = new Date(apt.date + 'T00:00:00Z').getTime();
          const target = new Date(date + 'T00:00:00Z').getTime();
          const weeks = Math.floor((target - anchor) / (7 * 24 * 60 * 60 * 1000));
          if (weeks < 0 || weeks % 2 !== 0) return false;
        }

        return true;
      }

      return false;
    });
  }

  const isInDragSelection = (day: string, slotIndex: number) => {
    if (!isDragging || !dragStart || !dragEnd || dragStart.day !== dragEnd.day) return false
    if (day !== dragStart.day) return false
    
    const minIndex = Math.min(dragStart.slotIndex, dragEnd.slotIndex)
    const maxIndex = Math.max(dragStart.slotIndex, dragEnd.slotIndex)
    return slotIndex >= minIndex && slotIndex <= maxIndex
  }

  const isTimeInSlot = (time: string, slot: TimeSlot) => {
    if (!slot.enabled) return false
    const [checkHour, checkMinute] = time.split(':').map(Number)
    const [startHour, startMinute] = slot.start.split(':').map(Number)
    const [endHour, endMinute] = slot.end.split(':').map(Number)
    
    const checkTime = checkHour * 60 + checkMinute
    const startTime = startHour * 60 + startMinute
    const endTime = endHour * 60 + endMinute
    
    return checkTime >= startTime && checkTime < endTime
  }

  const isAppointmentInSlot = (time: string, appointments: PrivateAppointment[]) => {
    const [checkHour, checkMinute] = time.split(':').map(Number)
    const checkTime = checkHour * 60 + checkMinute
    
    for (const apt of appointments) {
      const [startHour, startMinute] = apt.startTime.split(':').map(Number)
      const [endHour, endMinute] = apt.endTime.split(':').map(Number)
      const startTime = startHour * 60 + startMinute
      const endTime = endHour * 60 + endMinute
      
      if (checkTime >= startTime && checkTime < endTime) {
        return apt
      }
    }
    return null
  }

  const renderWorkingHoursCompact = () => {
    // weekDates (not WEEK_DAYS) so each card can show its actual date in
    // week mode; in recurrent mode date is '' and the chip is hidden.
    const visibleDays = showWeekendDays
      ? weekDates
      : weekDates.filter(day => day.key !== 'saturday' && day.key !== 'sunday')

    return (
      <div className="working-hours-compact">
        {/* Header row: section title left, controls (weekend toggle +
            clean/edit mode switch) right. */}
        <div className="working-hours-compact-header">
          <span className="working-hours-compact-title">Disponibilités rapides</span>
          <div className="days-config-controls">
            <button
              className="toggle-weekend-button"
              onClick={() => setShowWeekendDays(!showWeekendDays)}
              title={showWeekendDays ? 'Masquer le week-end' : 'Afficher le week-end'}
            >
              <svg
                className="toggle-weekend-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {showWeekendDays
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />}
              </svg>
            </button>
            {!isLocked && (
              <button
                className={`edit-mode-button ${isEditMode ? 'is-active' : ''}`}
                onClick={() => {
                  if (isEditMode) {
                    // Leaving edit mode — drop any in-progress hour edit.
                    setEditingDay(null)
                    setEditingWorkingHours(null)
                  }
                  setIsEditMode(!isEditMode)
                }}
                title={isEditMode ? 'Terminer la modification' : 'Modifier le planning'}
              >
                <svg className="toggle-weekend-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isEditMode
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.862 4.487z" />}
                </svg>
                <span>{isEditMode ? 'Terminer' : 'Modifier'}</span>
              </button>
            )}
          </div>
        </div>
        <div className="days-config-compact">
          {visibleDays.map(day => {
            const slot = getSlot(day.key)
            const workLocation = getWorkLocation(day.key)
            const isEditing = editingDay === day.key

            return (
              <div
                key={day.key}
                className={`day-config-compact ${slot?.enabled ? 'enabled' : 'disabled'} ${isEditing ? 'editing' : ''} ${readOnly ? 'readonly' : ''}`}
                onClick={() => handleDayClick(day.key)}
              >
                <div className="day-compact-header">
                  <span className="day-compact-daylabel">
                    <span className="day-compact-name">{day.short}</span>
                    {mode === 'week' && day.date && (
                      <span className="day-compact-date">{day.date.slice(8, 10)}/{day.date.slice(5, 7)}</span>
                    )}
                    {availability[day.key]?.isHoliday && (
                      <span
                        className="day-compact-holiday"
                        title={isEmployee
                          ? "Férié — jour déchargé automatiquement, base journalière créditée. Si vous avez réellement travaillé, activez le jour et encodez vos heures : elles s'ajoutent au crédit."
                          : "Férié — en tant qu'indépendant(e), pas de crédit automatique : votre horaire s'applique normalement et seules les heures encodées comptent. Désactivez le jour si vous ne travaillez pas."}
                      >Férié</span>
                    )}
                  </span>
                  {!readOnly && (
                    <label className="expert-pref-toggle-switch-compact" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={slot?.enabled || false}
                        onChange={() => handleTimeSlotToggle(day.key)}
                      />
                      <span className="expert-pref-toggle-slider-compact"></span>
                    </label>
                  )}
                </div>

                {slot?.enabled && (
                  <div className="day-compact-content">
                    {!isEditing ? (
                      <>
                        <div className="time-display">
                          {slot.start} - {slot.end}
                        </div>
                        {readOnly ? (
                          <span className={`work-location-static ${workLocation === 'remote' ? 'is-remote' : ''}`}>
                            {workLocation === 'remote' ? 'Télétravail' : 'Bureau'}
                          </span>
                        ) : (
                        <div className="work-location-toggle" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`work-location-btn ${workLocation === 'office' ? 'active' : ''}`}
                            onClick={() => handleWorkLocationChange(day.key, 'office')}
                            title="Bureau"
                            disabled={isSaving}
                          >
                            <svg
                              className="work-location-icon"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            <span className="work-location-text">Bureau</span>
                          </button>
                          <button
                            className={`work-location-btn ${workLocation === 'remote' ? 'active' : ''}`}
                            onClick={() => handleWorkLocationChange(day.key, 'remote')}
                            title="Télétravail"
                            disabled={isSaving}
                          >
                            <svg
                              className="work-location-icon"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                              />
                            </svg>
                            <span className="work-location-text">Télétravail</span>
                          </button>
                        </div>
                        )}
                      </>
                    ) : (
                      <div className="day-compact-edit-inputs" onClick={(e) => e.stopPropagation()}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '4px'
                        }}>
                          <input
                            type="time"
                            value={editingWorkingHours?.day === day.key ? editingWorkingHours.startTime : slot.start}
                            onChange={(e) => {
                              e.stopPropagation()
                              const newStart = e.target.value
                              let currentEnd = editingWorkingHours?.day === day.key ? editingWorkingHours.endTime : slot.end

                              // If new start >= current end, adjust end to be 1 hour after start
                              const [startH, startM] = newStart.split(':').map(Number)
                              const [endH, endM] = currentEnd.split(':').map(Number)
                              const startMins = startH * 60 + startM
                              const endMins = endH * 60 + endM

                              if (startMins >= endMins) {
                                const newEndMins = Math.min(startMins + 60, 24 * 60) // Add 1 hour, max 24:00
                                const newEndH = Math.floor(newEndMins / 60)
                                const newEndM = newEndMins % 60
                                currentEnd = `${newEndH.toString().padStart(2, '0')}:${newEndM.toString().padStart(2, '0')}`
                              }

                              setEditingWorkingHours({
                                day: day.key,
                                startTime: newStart,
                                endTime: currentEnd
                              })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleConfirmWorkingHours()
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            min="06:00"
                            max="23:59"
                            style={{
                              border: '1px solid #d1d5db',
                              borderRadius: '3px',
                              padding: '2px 4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#244886',
                              width: '65px',
                              cursor: 'text'
                            }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '10px' }}>-</span>
                          <input
                            type="time"
                            value={editingWorkingHours?.day === day.key ? editingWorkingHours.endTime : slot.end}
                            onChange={(e) => {
                              e.stopPropagation()
                              const newEnd = e.target.value
                              let currentStart = editingWorkingHours?.day === day.key ? editingWorkingHours.startTime : slot.start

                              // If new end <= current start, adjust start to be 1 hour before end
                              const [startH, startM] = currentStart.split(':').map(Number)
                              const [endH, endM] = newEnd.split(':').map(Number)
                              const startMins = startH * 60 + startM
                              const endMins = endH * 60 + endM

                              if (endMins <= startMins) {
                                const newStartMins = Math.max(endMins - 60, 6 * 60) // Subtract 1 hour, min 06:00
                                const newStartH = Math.floor(newStartMins / 60)
                                const newStartM = newStartMins % 60
                                currentStart = `${newStartH.toString().padStart(2, '0')}:${newStartM.toString().padStart(2, '0')}`
                              }

                              setEditingWorkingHours({
                                day: day.key,
                                startTime: currentStart,
                                endTime: newEnd
                              })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleConfirmWorkingHours()
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            min="06:00"
                            max="23:59"
                            style={{
                              border: '1px solid #d1d5db',
                              borderRadius: '3px',
                              padding: '2px 4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#244886',
                              width: '65px',
                              cursor: 'text'
                            }}
                          />
                        </div>
                        <div style={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>
                          ou dessinez ci-dessous
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

        </div>
      </div>
    )
  }

  const renderTimelineView = useMemo(() => {
    // Only render hour lines, but keep 15-minute data for interaction
    const hours = Array.from({ length: 18 }, (_, i) => i + 6) // 6 AM to 11 PM

    // Filter out weekend days if they are both disabled
    const saturdaySlot = getSlot('saturday')
    const sundaySlot = getSlot('sunday')
    const hideWeekend = !saturdaySlot?.enabled && !sundaySlot?.enabled

    const visibleDays = hideWeekend
      ? weekDates.filter(day => day.key !== 'saturday' && day.key !== 'sunday')
      : weekDates
    
    // Process appointments to create visual blocks
    const getAppointmentBlocks = (dayKey: string, date: string) => {
      const dayAppointments = getAppointmentsForDay(date, dayKey)
      return dayAppointments.map(original => {
        // During a resize the in-progress times live in resizingAppointment;
        // the prop list is only updated once on mouse-up.
        const apt = resizingAppointment && original.id === resizingAppointment.id
          ? resizingAppointment
          : original
        const [startHour, startMinute] = apt.startTime.split(':').map(Number)
        const [endHour, endMinute] = apt.endTime.split(':').map(Number)
        
        // Calculate position based on hours from 6 AM
        const startHourOffset = Math.max(0, startHour - 6)
        const endHourOffset = Math.max(0, endHour - 6)
        
        // Each hour row is 44px tall (updated for better readability)
        const rowHeight = 44
        const topPosition = (startHourOffset * rowHeight) + ((startMinute / 60) * rowHeight)
        const endPosition = (endHourOffset * rowHeight) + ((endMinute / 60) * rowHeight)
        const height = Math.max(20, endPosition - topPosition) // Minimum height of 20px
        
        return {
          ...apt,
          top: topPosition,
          height: height
        }
      })
    }
    
    return (
      <div className="availability-timeline" ref={timelineRef}>
        <div className="timeline-header">
          <div className="timeline-corner">Heures</div>
          {visibleDays.map(day => (
            <div key={day.key} className={`timeline-day-header ${day.isToday ? 'today' : ''}`}>
              <div className="day-label">{day.short}</div>
              {mode === 'week' && day.dayNumber && (
                <>
                  <div className="day-number">{day.dayNumber}</div>
                  <div className="day-month">{day.month}</div>
                </>
              )}
            </div>
          ))}
        </div>
        
        <div className="timeline-body">
          {hours.map(hour => {
            const hourSlotIndex = (hour - 6) * 4 // Index in TIME_SLOTS array
            
            return (
              <div key={hour} className="timeline-row hour-line">
                <div className="timeline-hour">
                  {`${hour.toString().padStart(2, '0')}:00`}
                </div>
                {visibleDays.map(day => {
                  const slot = getSlot(day.key)
                  
                  // Check working hours for the entire hour
                  let isAnyWorking = false
                  const selectedQuarters = []
                  
                  for (let quarter = 0; quarter < 4; quarter++) {
                    const slotIndex = hourSlotIndex + quarter
                    const timeSlot = TIME_SLOTS[slotIndex]
                    if (timeSlot) {
                      if (isTimeInSlot(timeSlot.time, slot)) {
                        isAnyWorking = true
                      }
                      if (isInDragSelection(day.key, slotIndex)) {
                        selectedQuarters.push(quarter)
                      }
                    }
                  }
                  
                  // Check if any appointments exist in this hour
                  const appointments = getAppointmentsForDay(day.date, day.key)
                  const hasAppointmentQuarters = []
                  for (let quarter = 0; quarter < 4; quarter++) {
                    const slotIndex = hourSlotIndex + quarter
                    const timeSlot = TIME_SLOTS[slotIndex]
                    if (timeSlot && isAppointmentInSlot(timeSlot.time, appointments)) {
                      hasAppointmentQuarters.push(quarter)
                    }
                  }
                  
                  // Create invisible quarter divs for interaction
                  const quarterDivs = []
                  for (let quarter = 0; quarter < 4; quarter++) {
                    const slotIndex = hourSlotIndex + quarter
                    const hasAppointment = hasAppointmentQuarters.includes(quarter)
                    
                    quarterDivs.push(
                      <div
                        key={`${day.key}-${hour}-${quarter}`}
                        className="timeline-quarter-invisible"
                        onMouseDown={(e) => {
                          // Disable interaction when buttons are showing for this day
                          if (editingWorkingHours && editingWorkingHours.day === day.key) {
                            return
                          }
                          e.stopPropagation()
                          handleMouseDown(day.key, slotIndex)
                        }}
                        onMouseEnter={() => {
                          // Disable interaction when buttons are showing for this day
                          if (editingWorkingHours && editingWorkingHours.day === day.key) {
                            return
                          }
                          handleMouseMove(day.key, slotIndex)
                        }}
                        style={{
                          position: 'absolute',
                          top: `${quarter * 25}%`,
                          left: (isAnyWorking && editingDay !== day.key) ? 3 : 0,
                          right: 0,
                          height: '25%',
                          zIndex: hasAppointment ? 6 : 2,
                          background: selectedQuarters.includes(quarter) ? 'rgba(59, 130, 246, 0.25)' : 'transparent',
                          cursor: (editingWorkingHours && editingWorkingHours.day === day.key) ? 'default' : (hasAppointment ? 'pointer' : 'crosshair'),
                          pointerEvents: (editingWorkingHours && editingWorkingHours.day === day.key) ? 'none' : 'auto'
                        }}
                      />
                    )
                  }
                  
                  return (
                    <div
                      key={`${day.key}-${hour}`}
                      className="timeline-cell-hour"
                      style={{
                        position: 'relative'
                      }}
                    >
                      {quarterDivs}
                    </div>
                  )
                })}
              </div>
            )
          })}
          
          {/* Appointments container for proper positioning */}
          <div className="appointments-container">
            {/* Saved working hours blocks - precise display */}
            {visibleDays.map((day, dayIndex) => {
              // Skip if this day is being edited (editing block will show instead)
              if (editingWorkingHours && editingWorkingHours.day === day.key) return null

              const slot = getSlot(day.key)
              if (!slot.enabled || !slot.start || !slot.end) return null

              const [startHour, startMinute] = slot.start.split(':').map(Number)
              const [endHour, endMinute] = slot.end.split(':').map(Number)

              const startHourOffset = Math.max(0, startHour - 6)
              const endHourOffset = Math.max(0, endHour - 6)

              const rowHeight = 44
              const topPosition = (startHourOffset * rowHeight) + ((startMinute / 60) * rowHeight)
              const endPosition = (endHourOffset * rowHeight) + ((endMinute / 60) * rowHeight)
              const height = Math.max(0, endPosition - topPosition)

              if (height <= 0) return null

              const totalDays = visibleDays.length
              const dayLeft = `calc(62px + ${dayIndex} * ((100% - 62px) / ${totalDays}) + 3px)`
              const innerWidth = `calc((100% - 62px) / ${totalDays} - 6px)`

              return (
                <div
                  key={`saved-working-hours-${day.key}`}
                  className="saved-working-hours-block"
                  style={{
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: dayLeft,
                    width: innerWidth,
                    height: `${height}px`,
                    background: 'rgba(36, 72, 134, 0.12)',
                    borderLeft: '3px solid rgba(36, 72, 134, 0.4)',
                    borderRadius: '2px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )
            })}

            {/* Working hours editing block */}
            {editingWorkingHours && visibleDays.map((day, dayIndex) => {
              if (day.key !== editingWorkingHours.day) return null

              const [startHour, startMinute] = editingWorkingHours.startTime.split(':').map(Number)
              const [endHour, endMinute] = editingWorkingHours.endTime.split(':').map(Number)

              const startHourOffset = Math.max(0, startHour - 6)
              const endHourOffset = Math.max(0, endHour - 6)

              const rowHeight = 44
              const topPosition = (startHourOffset * rowHeight) + ((startMinute / 60) * rowHeight)
              const endPosition = (endHourOffset * rowHeight) + ((endMinute / 60) * rowHeight)
              const height = Math.max(20, endPosition - topPosition)

              const totalDays = visibleDays.length
              const columnWidth = `calc((100% - 62px) / ${totalDays})`
              const dayLeft = `calc(62px + ${dayIndex} * ((100% - 62px) / ${totalDays}) + 2px)`
              const innerWidth = `calc(${columnWidth} - 6px)`

              // Validation: check if times are valid
              const isTimeValid = (start: string, end: string) => {
                const [startH, startM] = start.split(':').map(Number)
                const [endH, endM] = end.split(':').map(Number)
                const startMins = startH * 60 + startM
                const endMins = endH * 60 + endM
                return endMins > startMins && startMins >= 360 && endMins <= 1440 // 6:00-24:00
              }

              const timeInputValid = isTimeValid(editingWorkingHours.startTime, editingWorkingHours.endTime)

              return (
                <div
                  key={`working-hours-${day.key}`}
                  className="working-hours-edit-block"
                  style={{
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: dayLeft,
                    width: innerWidth,
                    height: `${height}px`,
                    minHeight: '70px',
                    background: 'rgba(36, 72, 134, 0.15)',
                    border: `2px solid ${timeInputValid ? 'rgba(36, 72, 134, 0.5)' : '#ef4444'}`,
                    borderRadius: '4px',
                    padding: '4px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pointerEvents: 'auto'
                  }}
                >
                  {/* Time Input Fields */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'white',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <input
                      type="time"
                      value={editingWorkingHours.startTime}
                      onChange={(e) => {
                        e.stopPropagation()
                        const newStart = e.target.value
                        setEditingWorkingHours(prev => {
                          if (!prev) return null
                          let newEnd = prev.endTime

                          // If new start >= current end, adjust end to be 1 hour after start
                          const [startH, startM] = newStart.split(':').map(Number)
                          const [endH, endM] = newEnd.split(':').map(Number)
                          const startMins = startH * 60 + startM
                          const endMins = endH * 60 + endM

                          if (startMins >= endMins) {
                            const newEndMins = Math.min(startMins + 60, 24 * 60)
                            const newEndH = Math.floor(newEndMins / 60)
                            const newEndM = newEndMins % 60
                            newEnd = `${newEndH.toString().padStart(2, '0')}:${newEndM.toString().padStart(2, '0')}`
                          }

                          return { ...prev, startTime: newStart, endTime: newEnd }
                        })
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && timeInputValid) {
                          e.preventDefault()
                          handleConfirmWorkingHours()
                        }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      min="06:00"
                      max="23:59"
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '3px',
                        padding: '2px 4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#244886',
                        width: '70px',
                        cursor: 'text'
                      }}
                    />
                    <span style={{ color: '#6b7280', fontWeight: 500 }}>-</span>
                    <input
                      type="time"
                      value={editingWorkingHours.endTime}
                      onChange={(e) => {
                        e.stopPropagation()
                        const newEnd = e.target.value
                        setEditingWorkingHours(prev => {
                          if (!prev) return null
                          let newStart = prev.startTime

                          // If new end <= current start, adjust start to be 1 hour before end
                          const [startH, startM] = newStart.split(':').map(Number)
                          const [endH, endM] = newEnd.split(':').map(Number)
                          const startMins = startH * 60 + startM
                          const endMins = endH * 60 + endM

                          if (endMins <= startMins) {
                            const newStartMins = Math.max(endMins - 60, 6 * 60)
                            const newStartH = Math.floor(newStartMins / 60)
                            const newStartM = newStartMins % 60
                            newStart = `${newStartH.toString().padStart(2, '0')}:${newStartM.toString().padStart(2, '0')}`
                          }

                          return { ...prev, startTime: newStart, endTime: newEnd }
                        })
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && timeInputValid) {
                          e.preventDefault()
                          handleConfirmWorkingHours()
                        }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      min="06:00"
                      max="23:59"
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '3px',
                        padding: '2px 4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#244886',
                        width: '70px',
                        cursor: 'text'
                      }}
                    />
                  </div>
                  {!timeInputValid && (
                    <div style={{ fontSize: '9px', color: '#ef4444', textAlign: 'center' }}>
                      Heures invalides
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px', position: 'relative', zIndex: 1001 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        if (timeInputValid) {
                          handleConfirmWorkingHours()
                        }
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                      disabled={isSaving || !timeInputValid}
                      style={{
                        padding: '6px 14px',
                        background: (isSaving || !timeInputValid) ? '#9ca3af' : '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: (isSaving || !timeInputValid) ? 'not-allowed' : 'pointer',
                        position: 'relative',
                        zIndex: 1002,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {isSaving ? (
                        <>
                          <span style={{
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            border: '2px solid white',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite'
                          }} />
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          OK
                        </>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleCancelWorkingHours()
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                      disabled={isSaving}
                      style={{
                        padding: '6px 12px',
                        background: isSaving ? '#9ca3af' : '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        position: 'relative',
                        zIndex: 1002,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })}

            {visibleDays.map((day, dayIndex) => {
              const appointmentBlocks = getAppointmentBlocks(day.key, day.date)
              const totalDays = visibleDays.length
              // Calculate exact column position with spacing
              const columnWidth = `calc((100% - 62px) / ${totalDays})` // 62px = 60px hour column + 2px margin
              const dayLeft = `calc(62px + ${dayIndex} * ((100% - 62px) / ${totalDays}) + 2px)` // Add 2px for inner padding
              const innerWidth = `calc(${columnWidth} - 6px)` // Reduce width for inner padding (3px each side)

              return appointmentBlocks.map(apt => {
                // Use different colors for professional vs personal appointments
                const isPro = apt.isProfessional;
                const bgColor = isPro ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 83, 67, 0.15)';
                const borderColor = isPro ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 83, 67, 0.3)';

                return (
                <div
                  key={`${apt.id}-${day.key}`}
                  className={`appointment-block ${mode === 'week' && apt.recurring && !apt.overrideDate ? 'recurring-readonly' : ''} ${isPro ? 'professional' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (readOnly) return
                    // Don't open modal if we're interacting with a control (resize/delete)
                    if (isInteractingWithControl.current) {
                      return
                    }
                    // In week mode, recurring appointments (templates) cannot be edited
                    if (mode === 'week' && apt.recurring && !apt.overrideDate) {
                      return // Do nothing - user must delete and recreate
                    }
                    setEditingAppointment(apt)
                    setShowAppointmentModal(true)
                  }}
                  style={{
                    position: 'absolute',
                    top: `${apt.top}px`,
                    left: dayLeft,
                    width: innerWidth,
                    height: `${apt.height}px`,
                    minHeight: '20px',
                    background: bgColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '4px',
                    padding: '2px 4px',
                    cursor: 'pointer',
                    zIndex: 50, // Higher z-index so it's always above calendar cells
                    display: 'flex',
                    alignItems: 'flex-start',
                    overflow: 'visible' // Allow resize handles and delete button to be visible
                  }}
                  title={`${apt.title} (${apt.startTime} - ${apt.endTime})${isPro ? ' - Professionnel' : ''}${mode === 'week' && apt.recurring && !apt.overrideDate ? ' - Récurrent (lecture seule)' : ''}`}
                >
                  {/* Top resize handle - disabled for recurring in week mode + locked weeks */}
                  {!readOnly && !(mode === 'week' && apt.recurring && !apt.overrideDate) && (
                    <div
                      className="appointment-resize-handle top"
                      onMouseDown={(e) => handleResizeStart(e, apt, 'top')}
                    />
                  )}

                  {/* Delete button - hidden on locked weeks */}
                  {!readOnly && (
                    <button
                      className="appointment-delete-btn"
                      onClick={(e) => handleDeleteClick(e, apt.id, day.date)}
                      aria-label="Supprimer"
                      title="Supprimer"
                    >
                      ✕
                    </button>
                  )}

                  <div className="appointment-content">
                    <div className="appointment-title">{apt.title}</div>
                    {apt.height > 30 && (
                      <div className="appointment-time">{apt.startTime} - {apt.endTime}</div>
                    )}
                  </div>

                  {/* Bottom resize handle - disabled for recurring in week mode + locked weeks */}
                  {!readOnly && !(mode === 'week' && apt.recurring && !apt.overrideDate) && (
                    <div
                      className="appointment-resize-handle bottom"
                      onMouseDown={(e) => handleResizeStart(e, apt, 'bottom')}
                    />
                  )}
                </div>
              )})
            })}
          </div>
        </div>
      </div>
    )
  }, [weekDates, availability, privateAppointments, isDragging, dragStart, dragEnd, mode, editingWorkingHours, editingDay, resizingAppointment, readOnly])

  return (
    <div className="availability-calendar" ref={calendarRef}>
      {renderWorkingHoursCompact()}

      <div className="calendar-content">
        {renderTimelineView}
      </div>
      
      {showAppointmentModal && (
        <AppointmentModal
          appointment={editingAppointment}
          domicileAddresses={domicileAddresses}
          selectedTimeSlot={selectedTimeSlot}
          mode={mode}
          onSave={handleAddAppointment}
          onClose={() => {
            setShowAppointmentModal(false)
            setEditingAppointment(null)
            setSelectedTimeSlot(null)
          }}
          onDelete={editingAppointment ? () => {
            handleDeleteAppointment(editingAppointment.id, editingAppointment.date)
            setShowAppointmentModal(false)
            setEditingAppointment(null)
            setSelectedTimeSlot(null)
          } : undefined}
        />
      )}
    </div>
  )
}

// Appointment Modal Component
interface AppointmentModalProps {
  appointment: PrivateAppointment | null
  domicileAddresses: Array<{ id: number; name: string; street?: string; city?: string }>
  selectedTimeSlot: { day: string; date: string; startTime: string; endTime: string } | null
  mode?: 'recurrent' | 'week'
  onSave: (appointment: PrivateAppointment) => void
  onClose: () => void
  onDelete?: () => void
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  appointment,
  domicileAddresses: _domicileAddresses,
  selectedTimeSlot,
  mode = 'week',
  onSave,
  onClose,
  onDelete
}) => {
  // Helper to generate a representative date for a given day of week
  const getRepresentativeDateForDay = (dayKey: string): string => {
    const dayMap: Record<string, number> = {
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6,
      'sunday': 0
    }

    const today = new Date()
    const currentDay = today.getDay()
    const targetDay = dayMap[dayKey] || 1

    // Calculate days to add to get to the target day
    let daysToAdd = targetDay - currentDay
    if (daysToAdd < 0) daysToAdd += 7

    // Use addDays instead of setDate to avoid DST issues
    const targetDate = addDays(today, daysToAdd)

    return format(targetDate, 'yyyy-MM-dd')
  }

  const getInitialFormData = () => {
    if (appointment) {
      return appointment
    }

    if (selectedTimeSlot) {
      // In recurrent mode, generate a representative date from dayOfWeek
      const date = mode === 'recurrent'
        ? getRepresentativeDateForDay(selectedTimeSlot.day)
        : selectedTimeSlot.date

      return {
        title: '',
        date: date,
        dayOfWeek: selectedTimeSlot.day,
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime,
        recurring: mode === 'recurrent', // Auto-enable recurring in recurrent mode
        recurrencePattern: 'weekly' as const,
        isProfessional: false
      }
    }

    return {
      title: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      dayOfWeek: 'monday',
      startTime: '09:00',
      endTime: '10:00',
      recurring: mode === 'recurrent', // Auto-enable recurring in recurrent mode
      recurrencePattern: 'weekly' as const,
      isProfessional: false
    }
  }
  
  const [formData, setFormData] = useState<Partial<PrivateAppointment>>(getInitialFormData())

  // Times are HH:MM zero-padded strings — lexicographic compare is correct.
  const timesValid = !!formData.startTime && !!formData.endTime && formData.endTime > formData.startTime

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!timesValid) return
    onSave(formData as PrivateAppointment)
  }

  // dayOfWeek must always match the date — recurring expansion, biweekly
  // anchoring and rendering all assume the anchor date falls on the
  // recurrence weekday. (The backend also derives it, this keeps local
  // state coherent before the round-trip.)
  const handleDateChange = (newDate: string) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const d = parseISO(newDate)
    const dayOfWeek = Number.isNaN(d.getTime()) ? formData.dayOfWeek : dayNames[d.getDay()]
    setFormData({ ...formData, date: newDate, dayOfWeek })
  }

  // Calculate duration
  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const [startHour, startMin] = formData.startTime.split(':').map(Number)
      const [endHour, endMin] = formData.endTime.split(':').map(Number)
      const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin)
      if (duration > 0) {
        const hours = Math.floor(duration / 60)
        const minutes = duration % 60
        return hours > 0 ? `${hours}h${minutes > 0 ? minutes : ''}` : `${minutes}min`
      }
    }
    return ''
  }

  return (
    <div className="preference-planning-modal-overlay" onClick={onClose}>
      <div className="preference-planning-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preference-planning-modal-header">
          <h2 className="preference-planning-modal-title">
            {appointment ? 'Modifier' : 'Nouveau'} rendez-vous privé
          </h2>
          <button className="preference-planning-modal-close" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="preference-planning-form">
          {/* Title Field */}
          <div className="preference-planning-form-group">
            <label className="preference-planning-label">Titre</label>
            <input
              type="text"
              className="preference-planning-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ex: Rendez-vous médecin, Réunion client..."
              required
              autoFocus
            />
          </div>

          {/* Date and Time Row */}
          <div className="preference-planning-datetime-row">
            {/* Hide date field in recurrent mode */}
            {mode !== 'recurrent' && (
              <div className="preference-planning-form-group">
                <label className="preference-planning-label">Date</label>
                <input
                  type="date"
                  className="preference-planning-input"
                  value={formData.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  required={!formData.recurring}
                  disabled={formData.recurring}
                />
              </div>
            )}

            <div className="preference-planning-form-group">
              <label className="preference-planning-label">Début</label>
              <input
                type="time"
                className="preference-planning-input"
                step="900"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>

            <div className="preference-planning-form-group">
              <label className="preference-planning-label">Fin</label>
              <input
                type="time"
                className="preference-planning-input"
                step="900"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>

            {calculateDuration() && (
              <div className="preference-planning-duration">
                <span className="preference-planning-duration-value">{calculateDuration()}</span>
              </div>
            )}
          </div>

          {!timesValid && formData.startTime && formData.endTime && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '-8px' }}>
              L'heure de fin doit être après l'heure de début.
            </div>
          )}

          {/* Recurring Option - Hide in recurrent mode as it's always recurring */}
          {mode !== 'recurrent' && (
            <div className="preference-planning-recurring-section">
              <label className="preference-planning-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.recurring}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                />
                <span className="preference-planning-checkbox-custom"></span>
                <span>Rendez-vous récurrent</span>
              </label>

              {formData.recurring && (
                <select
                  className="preference-planning-select"
                  value={formData.recurrencePattern}
                  onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value as any })}
                >
                  <option value="weekly">Chaque semaine</option>
                  <option value="biweekly">Toutes les 2 semaines</option>
                </select>
              )}
            </div>
          )}

          {/* Show recurrence pattern selector in recurrent mode */}
          {mode === 'recurrent' && (
            <div className="preference-planning-recurring-section">
              <label className="preference-planning-label">Fréquence de récurrence</label>
              <select
                className="preference-planning-select"
                value={formData.recurrencePattern}
                onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value as any })}
              >
                <option value="weekly">Chaque semaine</option>
                <option value="biweekly">Toutes les 2 semaines</option>
              </select>
            </div>
          )}

          {/* Professional appointment option */}
          <div className="preference-planning-recurring-section">
            <label className="preference-planning-checkbox-label">
              <input
                type="checkbox"
                checked={formData.isProfessional || false}
                onChange={(e) => setFormData({ ...formData, isProfessional: e.target.checked })}
              />
              <span className="preference-planning-checkbox-custom"></span>
              <span>Rendez-vous professionnel</span>
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 24px' }}>
              Le temps compte dans les heures travaillées (ex: réunion client, formation)
            </p>
          </div>

          <div className="preference-planning-modal-footer">
            {onDelete && (
              <button 
                type="button" 
                onClick={onDelete} 
                className="preference-planning-btn preference-planning-btn-danger"
              >
                Supprimer
              </button>
            )}
            <div className="preference-planning-footer-actions">
              <button 
                type="button" 
                onClick={onClose}
                className="preference-planning-btn preference-planning-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="preference-planning-btn preference-planning-btn-primary"
                disabled={!timesValid}
              >
                {appointment ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}